import { useEffect, useState } from "react";
import {
  Card, Stack, Table, Badge, Loader, Center, Tabs, Group, Text,
  SegmentedControl, Checkbox, Switch, Button, Alert, SimpleGrid, Divider,
  ScrollArea, Modal, Select, ActionIcon,
} from "@mantine/core";
import { IconLock, IconDeviceFloppy, IconRotate, IconShieldLock } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  useResource, useRbacMatrix, useUpdateRbacMatrix, apiClient,
} from "../../../shared/api";
import { PageHeader, EmptyState } from "../../../shared/ui";

export function UsersPage() {
  const { data: memData, isLoading } = useResource<any>("memberships");
  const memberships: any[] = memData?.results || memData || [];
  // Kişiye özel yetki modalı için seçili üyelik.
  const [permMember, setPermMember] = useState<any | null>(null);

  if (isLoading) return <Center h={300}><Loader /></Center>;

  return (
    <Stack gap="md">
      <PageHeader title="Kullanıcılar & RBAC" subtitle={`${memberships.length} üyelik`} />
      <Tabs defaultValue="users">
        <Tabs.List>
          <Tabs.Tab value="users">Kullanıcılar</Tabs.Tab>
          <Tabs.Tab value="permissions">İzin Matrisi</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="users" pt="md">
          {memberships.length === 0 ? <EmptyState title="Üyelik yok" /> : (
            <Card padding="sm" radius="md" withBorder>
              <Table.ScrollContainer minWidth={520}>
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Kullanıcı</Table.Th>
                      <Table.Th>E-posta</Table.Th>
                      <Table.Th>Rol</Table.Th>
                      <Table.Th>Şube</Table.Th>
                      <Table.Th>Aktif</Table.Th>
                      <Table.Th ta="right">Özel Yetki</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {memberships.map((m: any) => (
                      <Table.Tr key={m.id}>
                        <Table.Td fw={500}>{m.username}</Table.Td>
                        <Table.Td>{m.email || "-"}</Table.Td>
                        <Table.Td><Badge>{m.role}</Badge></Table.Td>
                        <Table.Td>{m.branch_name || "Tüm Şubeler"}</Table.Td>
                        <Table.Td>{m.is_active ? <Badge color="green">Aktif</Badge> : <Badge color="gray">Pasif</Badge>}</Table.Td>
                        <Table.Td ta="right">
                          {m.role === "OWNER" ? (
                            <Text size="xs" c="dimmed">tam yetki</Text>
                          ) : (
                            <ActionIcon
                              variant="subtle"
                              title="Bu kullanıcıya özel yetki ver/kıs"
                              onClick={() => setPermMember(m)}
                            >
                              <IconShieldLock size={16} />
                            </ActionIcon>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Card>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="permissions" pt="md">
          <PermissionMatrixEditor />
        </Tabs.Panel>
      </Tabs>

      <MembershipPermissionModal
        member={permMember}
        memberships={memberships}
        onClose={() => setPermMember(null)}
      />
    </Stack>
  );
}

/** Kişiye özel yetki düzenleme + başka kullanıcıdan klonlama (owner-only).
 *  Rol matrisi değişmez; SADECE bu üyenin efektif yetkisi ayarlanır. */
function MembershipPermissionModal({
  member, memberships, onClose,
}: { member: any | null; memberships: any[]; onClose: () => void }) {
  const { data: meta } = useRbacMatrix();
  const [cells, setCells] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cloneFrom, setCloneFrom] = useState<string | null>(null);

  useEffect(() => {
    if (!member) return;
    setLoading(true);
    apiClient.get(`/api/v1/rbac/membership/${member.id}/`)
      .then(({ data }) => {
        const s = new Set<string>();
        for (const [res, acts] of Object.entries<any>(data.matrix || {})) {
          for (const a of acts) s.add(`${res}.${a}`);
        }
        setCells(s);
      })
      .catch(() => notifications.show({ message: "Yetkiler okunamadı", color: "red" }))
      .finally(() => setLoading(false));
  }, [member]);

  if (!member) return null;
  const resources: any[] = meta?.resources || [];
  const actions: any[] = meta?.actions || [];

  function toggle(cell: string) {
    setCells((prev) => {
      const next = new Set(prev);
      next.has(cell) ? next.delete(cell) : next.add(cell);
      return next;
    });
  }

  async function save() {
    setSaving(true);
    try {
      const matrix: Record<string, string[]> = {};
      for (const cell of cells) {
        const [res, act] = cell.split(".");
        (matrix[res] ||= []).push(act);
      }
      await apiClient.put(`/api/v1/rbac/membership/${member.id}/`, { matrix });
      notifications.show({ message: "Kişiye özel yetki kaydedildi", color: "green" });
      onClose();
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    } finally {
      setSaving(false);
    }
  }

  async function doClone() {
    if (!cloneFrom) return;
    setSaving(true);
    try {
      await apiClient.post(`/api/v1/rbac/membership/${member.id}/clone/`, { source: cloneFrom });
      notifications.show({ message: "Yetkiler klonlandı", color: "green" });
      onClose();
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal opened={!!member} onClose={onClose} title={`Özel Yetki — ${member.username} (${member.role})`} size="xl">
      <Stack gap="sm">
        <Text size="xs" c="dimmed">
          Bu yetkiler SADECE bu kullanıcıya uygulanır; aynı roldeki diğer kullanıcılar
          etkilenmez. İşaretli hücreler kullanıcının sahip olacağı yetkilerdir.
        </Text>
        <Group align="flex-end" gap="sm">
          <Select
            label="Başka kullanıcıdan klonla"
            placeholder="Kaynak kullanıcı seç"
            data={memberships
              .filter((x) => x.id !== member.id)
              .map((x) => ({ value: x.id, label: `${x.username} (${x.role})` }))}
            value={cloneFrom}
            onChange={setCloneFrom}
            w={280}
            clearable
          />
          <Button variant="light" onClick={doClone} disabled={!cloneFrom} loading={saving}>
            Klonla
          </Button>
        </Group>
        <Divider />
        {loading ? <Center h={120}><Loader /></Center> : (
          <ScrollArea h={360}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Kaynak</Table.Th>
                  {actions.map((a) => <Table.Th key={a.value} ta="center">{a.label}</Table.Th>)}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {resources.map((res) => (
                  <Table.Tr key={res.value}>
                    <Table.Td fw={500}>{res.label}</Table.Td>
                    {actions.map((a) => {
                      const cell = `${res.value}.${a.value}`;
                      return (
                        <Table.Td key={a.value} ta="center">
                          <Checkbox
                            checked={cells.has(cell)}
                            onChange={() => toggle(cell)}
                          />
                        </Table.Td>
                      );
                    })}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Vazgeç</Button>
          <Button onClick={save} loading={saving} leftSection={<IconDeviceFloppy size={16} />}>
            Kaydet
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Düzenlenebilir rol × kaynak × eylem matrisi + uygulama erişimi.
 *  Owner her rolün yetkisini kendi tenant'ına göre değiştirir; OWNER kilitli. */
function PermissionMatrixEditor() {
  const { data, isLoading, isError } = useRbacMatrix();
  const update = useUpdateRbacMatrix();

  const [selectedRole, setSelectedRole] = useState<string>("WAITER");
  // Yerel taslak — kaydedilene kadar değişiklikler burada.
  // matrix[role] = Set("RESOURCE.ACTION"); appAccess[role] = Set(app)
  // menuHidden[role] = Set(menu_key) — GİZLENEN öğeler (yetkiden bağımsız).
  const [matrix, setMatrix] = useState<Record<string, Set<string>>>({});
  const [appAccess, setAppAccess] = useState<Record<string, Set<string>>>({});
  const [menuHidden, setMenuHidden] = useState<Record<string, Set<string>>>({});
  const [dirty, setDirty] = useState(false);

  // API verisini yerel Set yapısına çevir (ilk yükleme + her fetch).
  useEffect(() => {
    if (!data) return;
    const m: Record<string, Set<string>> = {};
    for (const [role, perRes] of Object.entries<any>(data.matrix || {})) {
      const cells = new Set<string>();
      for (const [res, acts] of Object.entries<any>(perRes)) {
        for (const a of acts) cells.add(`${res}.${a}`);
      }
      m[role] = cells;
    }
    const a: Record<string, Set<string>> = {};
    for (const [role, apps] of Object.entries<any>(data.app_access || {})) {
      a[role] = new Set(apps as string[]);
    }
    const h: Record<string, Set<string>> = {};
    for (const [role, keys] of Object.entries<any>(data.menu_hidden || {})) {
      h[role] = new Set(keys as string[]);
    }
    setMatrix(m);
    setAppAccess(a);
    setMenuHidden(h);
    setDirty(false);
  }, [data]);

  if (isLoading) return <Center h={200}><Loader /></Center>;
  if (isError || !data) {
    return (
      <Alert color="red" title="Yetki yok">
        Bu sayfayı yalnız restoran sahibi (OWNER) görüntüleyebilir.
      </Alert>
    );
  }

  const roles: any[] = data.roles || [];
  const resources: any[] = data.resources || [];
  const actions: any[] = data.actions || [];
  const apps: any[] = data.apps || [];
  const menuItems: any[] = data.menu_items || [];
  const isOwnerRole = selectedRole === "OWNER";
  const cells = matrix[selectedRole] || new Set<string>();
  const roleApps = appAccess[selectedRole] || new Set<string>();
  const roleHidden = menuHidden[selectedRole] || new Set<string>();
  // Menü Görünürlüğü tüm öğeleri Yönetim Paneli (ADMIN) öğeleridir; bu rolün
  // ADMIN erişimi yoksa zaten panele giremez → menü ayarları anlamsız.
  const hasAdminAccess = isOwnerRole || roleApps.has("ADMIN");

  // Menü öğelerini grup başlığına göre kümele (UI'da bölümlemek için).
  const menuByGroup: Record<string, any[]> = {};
  for (const mi of menuItems) {
    (menuByGroup[mi.group || "Genel"] ||= []).push(mi);
  }

  function toggleCell(resource: string, action: string) {
    if (isOwnerRole) return;
    const key = `${resource}.${action}`;
    setMatrix((prev) => {
      const next = { ...prev };
      const s = new Set(next[selectedRole] || []);
      if (s.has(key)) s.delete(key); else s.add(key);
      next[selectedRole] = s;
      return next;
    });
    setDirty(true);
  }

  function toggleApp(app: string) {
    if (isOwnerRole) return;
    setAppAccess((prev) => {
      const next = { ...prev };
      const s = new Set(next[selectedRole] || []);
      if (s.has(app)) s.delete(app); else s.add(app);
      next[selectedRole] = s;
      return next;
    });
    setDirty(true);
  }

  // Switch "Görünür" mantığında: checked=true → görünür (hidden setinden çıkar),
  // checked=false → gizli (hidden setine ekle).
  function toggleMenu(menuKey: string) {
    if (isOwnerRole) return;
    setMenuHidden((prev) => {
      const next = { ...prev };
      const s = new Set(next[selectedRole] || []);
      if (s.has(menuKey)) s.delete(menuKey); else s.add(menuKey);
      next[selectedRole] = s;
      return next;
    });
    setDirty(true);
  }

  // visible=true → tümünü göster (hidden seti boşalt); false → tümünü gizle.
  function setRoleMenuAll(visible: boolean) {
    if (isOwnerRole) return;
    setMenuHidden((prev) => ({
      ...prev,
      [selectedRole]: visible
        ? new Set<string>()
        : new Set<string>(menuItems.map((mi) => mi.key)),
    }));
    setDirty(true);
  }

  function resetToDefault() {
    // defaults: {role: {resource: [actions]}}, default_app_access: {role: [apps]}
    const m: Record<string, Set<string>> = {};
    for (const [role, perRes] of Object.entries<any>(data.defaults || {})) {
      const c = new Set<string>();
      for (const [res, acts] of Object.entries<any>(perRes)) {
        for (const a of acts) c.add(`${res}.${a}`);
      }
      m[role] = c;
    }
    const a: Record<string, Set<string>> = {};
    for (const [role, apps2] of Object.entries<any>(data.default_app_access || {})) {
      a[role] = new Set(apps2 as string[]);
    }
    setMatrix(m);
    setAppAccess(a);
    // Menü görünürlüğü varsayılanı: hiçbir öğe gizli değil.
    setMenuHidden({});
    setDirty(true);
  }

  function save() {
    // Set → API biçimine çevir. OWNER hariç tüm roller gönderilir.
    const matrixOut: Record<string, Record<string, string[]>> = {};
    const appOut: Record<string, string[]> = {};
    const menuOut: Record<string, string[]> = {};
    for (const r of roles) {
      if (!r.editable) continue;
      const perRes: Record<string, string[]> = {};
      for (const cell of matrix[r.value] || []) {
        const [res, act] = cell.split(".");
        (perRes[res] ||= []).push(act);
      }
      matrixOut[r.value] = perRes;
      appOut[r.value] = Array.from(appAccess[r.value] || []);
      menuOut[r.value] = Array.from(menuHidden[r.value] || []);
    }
    update.mutate(
      { matrix: matrixOut, app_access: appOut, menu_hidden: menuOut },
      {
        onSuccess: () => {
          notifications.show({ message: "Yetkiler kaydedildi", color: "green" });
          setDirty(false);
        },
        onError: (e: any) =>
          notifications.show({
            message: e?.response?.data?.detail || "Kaydedilemedi",
            color: "red",
          }),
      },
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap" gap="sm">
        <ScrollArea type="never" style={{ maxWidth: "100%" }}>
          <SegmentedControl
            value={selectedRole}
            onChange={setSelectedRole}
            data={roles.map((r) => ({ label: r.label, value: r.value }))}
          />
        </ScrollArea>
        <Group gap="xs">
          <Button
            variant="default"
            leftSection={<IconRotate size={16} />}
            onClick={resetToDefault}
          >
            Varsayılana Dön
          </Button>
          <Button
            leftSection={<IconDeviceFloppy size={16} />}
            disabled={!dirty}
            loading={update.isPending}
            onClick={save}
          >
            Kaydet
          </Button>
        </Group>
      </Group>

      {isOwnerRole && (
        <Alert color="blue" icon={<IconLock size={16} />}>
          OWNER (sahip) her zaman tüm yetkilere sahiptir ve düzenlenemez —
          kendinizi yanlışlıkla kilitlemekten korunursunuz.
        </Alert>
      )}

      {/* Uygulama erişimi */}
      <Card padding="md" radius="md" withBorder>
        <Text fw={700} mb="xs">Uygulama Erişimi</Text>
        <Text size="xs" c="dimmed" mb="sm">
          Bu rol hangi uygulamalara giriş yapabilir?
        </Text>
        <Group gap="lg">
          {apps.map((app) => (
            <Switch
              key={app.value}
              label={app.label}
              checked={isOwnerRole || roleApps.has(app.value)}
              disabled={isOwnerRole}
              onChange={() => toggleApp(app.value)}
            />
          ))}
        </Group>
      </Card>

      {/* Menü görünürlüğü — yetkiden bağımsız sol menü öğesi gizle/göster */}
      <Card padding="md" radius="md" withBorder>
        <Group justify="space-between" align="flex-start" mb="xs">
          <div>
            <Text fw={700}>Menü Görünürlüğü</Text>
            <Text size="xs" c="dimmed" maw={560}>
              Bu rol sol menüde hangi öğeleri görsün? Kapatmak öğeyi gizler;
              yetki (aşağıdaki matris) ayrı bir kavramdır — yetkisi olsa bile
              öğeyi menüden saklayabilirsiniz.
            </Text>
          </div>
          {!isOwnerRole && hasAdminAccess && (
            <Group gap="xs">
              <Button
                size="compact-xs"
                variant="subtle"
                onClick={() => setRoleMenuAll(true)}
              >
                Tümünü Göster
              </Button>
              <Button
                size="compact-xs"
                variant="subtle"
                color="gray"
                onClick={() => setRoleMenuAll(false)}
              >
                Tümünü Gizle
              </Button>
            </Group>
          )}
        </Group>

        {!hasAdminAccess ? (
          <Alert color="gray" mt="sm">
            Bu rolün <b>Yönetim Paneli</b> erişimi kapalı, dolayısıyla bu paneli
            hiç göremez — menü görünürlüğü ayarlanamaz. Önce yukarıdan
            “Yönetim Paneli” erişimini açın.
          </Alert>
        ) : (
          <Stack gap="md" mt="sm">
            {Object.entries(menuByGroup).map(([group, items]) => (
              <div key={group}>
                <Divider
                  label={group ? group.toUpperCase() : "GENEL"}
                  labelPosition="left"
                  mb="xs"
                />
                <SimpleGrid cols={{ base: 2, sm: 3, lg: 4 }} spacing="xs" verticalSpacing="xs">
                  {items.map((mi) => (
                    <Switch
                      key={mi.key}
                      label={mi.label}
                      size="sm"
                      checked={isOwnerRole || !roleHidden.has(mi.key)}
                      disabled={isOwnerRole}
                      onChange={() => toggleMenu(mi.key)}
                      styles={{ label: { fontSize: 13, lineHeight: 1.2 } }}
                    />
                  ))}
                </SimpleGrid>
              </div>
            ))}
          </Stack>
        )}
      </Card>

      {/* Resource × Action matrisi */}
      <Card padding="sm" radius="md" withBorder>
        <Table.ScrollContainer minWidth={460}>
          <Table highlightOnHover withRowBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th miw={140}>Kaynak</Table.Th>
                {actions.map((a) => (
                  <Table.Th key={a.value} w={72} ta="center">
                    {a.label}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {resources.map((res) => (
                <Table.Tr key={res.value}>
                  <Table.Td fw={500} miw={140}>{res.label}</Table.Td>
                  {actions.map((a) => {
                    const checked = isOwnerRole || cells.has(`${res.value}.${a.value}`);
                    return (
                      <Table.Td key={a.value} w={72}>
                        <Checkbox
                          checked={checked}
                          disabled={isOwnerRole}
                          onChange={() => toggleCell(res.value, a.value)}
                          styles={{
                            root: { display: "flex", justifyContent: "center" },
                            input: { cursor: isOwnerRole ? "not-allowed" : "pointer" },
                          }}
                        />
                      </Table.Td>
                    );
                  })}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
    </Stack>
  );
}
